import * as React from "react";
import {Route, RouteComponentProps} from "react-router";
import {connect} from "react-redux";
import {AppState} from "../RootReducer";
import {MessageState, MessageStatus} from "../messages/MessagesReducer";
import {match, Switch} from "react-router-dom";
import {fetchMessages, loadMoreMessages, markAllAsRead} from "../messages/MessageActions";
import {Dispatch} from "redux";
import {UserMessageMinimalProjection} from "../http/protocol";
import "./styles/MessagesPage.scss";
import {formatDate} from "../utils/date";
import MessageView from "./MessageView";
import {AccountCard} from "../ui/AccountUI";
import {t} from "../i18n";
import {BrandButton} from "../ui/buttons/buttons";
import {getMessagesCurrentOffset, getMessagesHasMore, getMessagesStatus} from "../selectors/messages-selector";

type AllProps = PropsFromState & PropsFromDispatch & OtherProps;

interface State {
    unreadMessages:number;
}

export class MessagesPage extends React.Component<AllProps, State> {
    constructor(props : AllProps) {
        super(props);
        this.state = {unreadMessages:0};
    }
    
    componentDidMount() {
        window.scroll(0,0)
        this.props.fetchMessages && this.props.fetchMessages();
    }

    openMessage(id? : number) {
        if(id) {
            this.props.history.push("/account/messages/"+id);
        }
    }

    loadMore = () => {
        if (this.props.hasMore && !this.props.isLoading && this.props.loadMoreMessages && this.props.currentOffset !== undefined) {
            this.props.loadMoreMessages(this.props.currentOffset);
        }
    }

    render() {
        const unreadCount = this.props.message?.unread ?? 0;
        
        return (
            <div className={"content-padding"}>
                <Switch>
                    <Route exact={true} path={"/account/messages"}>
                        { !this.props.selectedMessage && this.props.message && this.props.message.messages && 
                            <MessageList 
                                messages={this.props.message.messages} 
                                openMessage={(id) => this.openMessage(id)} 
                                markAllAsRead={() => this.props.markAllAsRead()} 
                                unread={unreadCount}
                                hasMore={this.props.hasMore}
                                isLoading={this.props.status === MessageStatus.LOADING}
                                loadMore={this.loadMore}
                            />
                        }
                    </Route>
                    <Route exact={true} path={"/account/messages/:id"}>
                        { this.props.selectedMessage && <MessageView message={this.props.selectedMessage} />}
                    </Route>
                </Switch>
            </div>
        )
    }
}

export const MessageList = (props : { 
    messages : UserMessageMinimalProjection[], 
    openMessage : (id? : number) => void, 
    markAllAsRead: () => void, 
    unread?:number,
    hasMore?: boolean,
    isLoading?: boolean,
    loadMore: () => void
}) => (
   <div>
       <div className={"section"}>
           <h4 className={"section"}>{t("menu.messages")}</h4>
           {
               props.unread && props.unread > 1 ? (
                   <span className="mark-all-as-read" style={{float:"right"}} onClick={() => props.markAllAsRead()}>{t("messages.mark-all-as-read")}</span>
               ) : null
           }
       </div>
       <div className={"message-list"}>
           {props.messages.map(m => (
               <MessagePreview key={m.id} message={m} openMessage={(id) => props.openMessage(id)}/>
           ))}
           
           {props.hasMore && (
               <div className="load-more-container">
                   <BrandButton 
                       className="btn btn-cta small" 
                       onClick={props.loadMore}
                       loading={props.isLoading}
                       disabled={props.isLoading}
                   >
                       {t("messages.load-more")}
                   </BrandButton>
               </div>
           )}
       </div>
   </div>
)

export const MessagePreview = (props : {message : UserMessageMinimalProjection, openMessage : (id? : number) => void }) => (
    <AccountCard onClick={() => props.openMessage(props.message.id)} className={"message-row " + (props.message.read ? "" : "unread")}>
       <div className={"row"}>
           <div className={"icon-container"}>
               {props.message.read && <i className={"material-icons"}>mail_outline</i>}
               {!props.message.read && <i className={"material-icons"}>mail</i>}
           </div>
           <div className={"message-preview-content"}>
               <div className={"title"}>
                   {props.message.title}
               </div>
               <div className={"date"}>
                   {formatDate(props.message.created)}
               </div>
           </div>
       </div>
    </AccountCard>
)

interface PropsFromState {
    message : MessageState;
    selectedMessage? : UserMessageMinimalProjection;
    hasMore?: boolean;
    currentOffset?: number;
    status?: MessageStatus;
    isLoading?: boolean;
}

interface PropsFromDispatch {
    fetchMessages? : () => void;
    loadMoreMessages?: (offset: number) => void;
    markAllAsRead : () => void;
}

interface OtherProps extends RouteComponentProps<{id:string}> {
}

const mapStateToProps = (state : AppState, ownProps : AllProps) : PropsFromState => {
    let message;
    if(ownProps.match.params.id && state.message && state.message.messages) {
        message = state.message.messages.find(m => ""+m.id == ownProps.match.params.id);
    }
    return {
        message : state.message,
        selectedMessage : message,
        hasMore: getMessagesHasMore(state),
        currentOffset: getMessagesCurrentOffset(state),
        status: getMessagesStatus(state),
        isLoading: getMessagesStatus(state) === MessageStatus.LOADING
    }
}

const mapDispatchToState = (dispatch : Dispatch) : PropsFromDispatch => {
    return { 
        fetchMessages: () => dispatch(fetchMessages() as any), 
        loadMoreMessages: (offset: number) => dispatch(loadMoreMessages(offset) as any),
        markAllAsRead: () => dispatch(markAllAsRead() as any)
    };
}

export default connect(mapStateToProps, mapDispatchToState)(MessagesPage as any);